import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { handleGetLaunchDateWindow } from "../../../utils/helperFunctions";
import {
  LAUNCH_DATE_SKIP_REASONS,
  handleSplitByLaunchDateWindow,
} from "../../../utils/constants/AdRequest";
import { GET_AD_REQUESTS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { Calendar } from "../../ui/calendar";
import toast from "react-hot-toast";
import AdsAPIs from "../../../apiRoutes/Ads";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import CustomModal from "../../common/CustomModal";

/** Moves the launch date of every selected request to one day. */
const BulkLaunchDateModal = ({ open, ids, requests, onClose, onDone }) => {
  const [date, setDate] = useState(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { earliest } = handleGetLaunchDateWindow({ launchDate: null });
  const split = handleSplitByLaunchDateWindow({ ids, requests });
  const skippedNotes = Object.entries(LAUNCH_DATE_SKIP_REASONS)
    .map(([key, reason]) => ({ count: split[key].length, reason }))
    .filter((n) => n.count > 0);
  const dateLabel = date ? format(date, "d MMM ''yy") : null;

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      AdsAPIs.handleBulkUpdateLaunchDate({
        axiosPrivate,
        ids: split.movable,
        launch_date: format(date, "yyyy-MM-dd"),
      }),
    onSuccess: (result) => {
      const moved = result?.updated?.length ?? 0;
      const skipped = result?.skipped ?? [];
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success(`Launch date set to ${dateLabel} for ${moved} request(s).`);
      if (skipped.length) {
        const reasons = [...new Set(skipped.map((s) => s.reason))].join("; ");
        toast.error(`${skipped.length} skipped: ${reasons}`);
      }
      onDone();
    },
  });

  const handleConfirm = () => {
    if (!date) return toast.error("Pick a launch date.");
    if (!split.movable.length)
      return toast.error("None of the selected requests can be moved.");
    mutate();
  };

  return (
    <CustomModal
      open={open}
      title="Change launch date"
      onClose={() => !isPending && onClose()}
      handleConfirmation={handleConfirm}
      handleConfirmationBtnText={
        split.movable.length
          ? `Change for ${split.movable.length}`
          : "Change date"
      }
      isLoading={isPending}
    >
      <div className="space-y-3 whitespace-normal py-1 text-sm text-gray-700">
        <p>
          {ids.length} request(s) selected. Pick the new launch date for all of
          them.
        </p>
        <div className="flex justify-center rounded-lg border border-gray-200">
          <div className="relative">
            <Calendar
              mode="single"
              selected={date}
              defaultMonth={parseISO(earliest)}
              disabled={{ before: parseISO(earliest) }}
              onSelect={setDate}
            />
          </div>
        </div>
        {dateLabel && split.movable.length > 0 && (
          <p className="rounded-lg bg-[#f6f5fe] px-3 py-2 text-[#3c3489]">
            {split.movable.length} request(s) will launch on{" "}
            <span className="font-semibold">{dateLabel}</span>.
          </p>
        )}
        {skippedNotes.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {skippedNotes.map((n) => (
              <p key={n.reason}>
                {n.count} will be skipped: {n.reason}.
              </p>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500">
          Each change is logged with your name and the time.
        </p>
      </div>
    </CustomModal>
  );
};

export default BulkLaunchDateModal;
